import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/ApiConfig';
import DefaultModal from './components/modals/DefaultModal';
import BancoModal from './components/modalServices/BancoModal';
import PageCard from './components/PageCard';
import LoadingModal from './components/modals/LoadingModal';
import usePermissions from './components/hooks/usePermissions';
import { useNavigate } from 'react-router-dom';

function Bancos() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, carregando: carregandoPermissoes } = usePermissions('bancos');
  
  const [bancos, setBancos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalData, setModalData] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [showBancoModal, setShowBancoModal] = useState(false);
  const [bancoToEdit, setBancoToEdit] = useState(null);
  const [selectedBanco, setSelectedBanco] = useState(null);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    fetchBancos();
  }, [podeAbrir, carregandoPermissoes]);

  const fetchBancos = async () => {
    setIsLoading(true);
    try {
      const axios = (await import('axios')).default;
      const response = await axios.get(`${API_BASE_URL.BANCO}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBancos(Array.isArray(response.data) ? response.data : []);
    } catch {
      setBancos([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectBanco = banco => {
    setSelectedBanco(
      selectedBanco && selectedBanco.id === banco.id ? null : banco
    );
  };

  const handleNewBanco = () => {
    setBancoToEdit(null);
    setShowBancoModal(true);
  };

  const handleEditBanco = () => {
    if (selectedBanco) {
      setBancoToEdit(selectedBanco);
      setShowBancoModal(true);
    }
  };

  const handleDeleteBanco = async () => {
    if (!selectedBanco) return;
    try {
      const axios = (await import('axios')).default;
      await axios.delete(
        `${API_BASE_URL.BANCO}/delete/${selectedBanco.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setModalData({
        show: true,
        type: 'success',
        message: 'Banco excluído com sucesso!',
      });
      fetchBancos();
      setSelectedBanco(null);
    } catch {
      setModalData({
        show: true,
        type: 'error',
        message: 'Erro ao excluir banco.',
      });
    }
  };

  const closeDefaultModal = () =>
    setModalData(prev => ({ ...prev, show: false }));

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard title="Bancos" icon="bi-bank2" scrollable={false}>
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Bancos.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            onClick={handleNewBanco}
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar bancos' : 'Novo banco'}
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            onClick={handleEditBanco}
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            disabled={!podeEditar || !selectedBanco}
            title={!podeEditar ? 'Você não tem permissão para editar' : !selectedBanco ? 'Selecione um banco' : 'Editar banco selecionado'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>
          <div className="vr mx-1" />
          <button
            onClick={handleDeleteBanco}
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            disabled={!podeExcluir || !selectedBanco}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !selectedBanco ? 'Selecione um banco' : 'Excluir banco selecionado'}
          >
            <i className="bi bi-trash me-1" />
            Excluir
          </button>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th className="px-2 align-middle" style={{ width: '20%' }}>
                  Código
                </th>
                <th className="px-2 align-middle">Nome</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                bancos.map(b => {
                  const isSelected =
                    selectedBanco && selectedBanco.id === b.id;
                  let rowClass = '';
                  let rowStyle = { cursor: 'pointer' };
                  if (isSelected) {
                    rowClass = 'table-active border-primary border-2';
                    rowStyle.backgroundColor = '#e3f2fd';
                    rowStyle.borderLeft = '4px solid #0d6efd';
                    rowStyle.boxShadow = '0 2px 4px rgba(13, 110, 253, 0.2)';
                  }
                  return (
                    <tr
                      key={b.id}
                      className={rowClass}
                      style={rowStyle}
                      onClick={() => handleSelectBanco(b)}
                    >
                      <td className="px-2">{b.id}</td>
                      <td className="px-2">{b.nome}</td>
                    </tr>
                  );
                })}
              {!isLoading && bancos.length === 0 && (
                <tr>
                  <td colSpan="2" className="text-center">
                    Nenhum banco encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <DefaultModal
        show={modalData.show}
        type={modalData.type}
        onClose={closeDefaultModal}
        message={modalData.message}
      />
      {showBancoModal && (
        <BancoModal
          show={showBancoModal}
          onClose={() => setShowBancoModal(false)}
          banco={bancoToEdit}
          onSaveSuccess={() => {
            setShowBancoModal(false);
            fetchBancos();
            setSelectedBanco(null);
          }}
        />
      )}
    </PageCard>
  );
}

export default Bancos;
